import { useTraduccion } from '@tallerya/i18n';
import { AvisoError, Boton, CampoSeleccion, CampoTexto, useAvisos, useFormulario, Ventana } from '@tallerya/ui';
import { useCambiosMarcas, useTiposDeActivo, useTodasLasMarcas, mismoNombre, version, type Marca } from './datos';

// Alta y renombre de una marca, en una ventana sobre la pantalla: el mismo
// patrón que «Nueva categoría» del Figma de Productos. Un campo para renombrar;
// dos para dar de alta, porque **el tipo se elige al crearla y no se cambia**.
//
// Avisa —sin impedir— cuando ya hay una marca que se llama igual con otro tipo:
// una «Toyota» genérica y una de vehículos conviven en la base, pero para quien
// elige en el combo son la misma, y casi siempre es un descuido.

export type Edicion = { tipo: 'nueva' } | { tipo: 'renombrar'; marca: Marca } | null;

interface Props {
  edicion: Edicion;
  onCerrar: () => void;
  /** Tras crear una marca, para dejarla elegida y mostrar sus modelos. */
  onCreada?: (id: string) => void;
  /**
   * El tipo con que nace la marca. Desde el formulario del activo (#119) es el
   * del activo: quien registra un compresor y crea «Bosch» la quiere para
   * compresores. Se puede cambiar antes de guardar.
   */
  tipoInicial?: string;
}

export function VentanaMarca({ edicion, onCerrar, onCreada, tipoInicial }: Props) {
  // La clave reinicia el formulario al abrir otra marca, o al volver a abrir.
  const clave = edicion === null ? 'cerrada' : edicion.tipo === 'nueva' ? 'nueva' : String(edicion.marca.id);
  return (
    <Contenido
      key={clave}
      edicion={edicion}
      onCerrar={onCerrar}
      {...(onCreada && { onCreada })}
      {...(tipoInicial && { tipoInicial })}
    />
  );
}

interface Valores {
  name: string;
  /** Un id de tipo, o vacío para una genérica. */
  assetTypeId: string;
}

function Contenido({ edicion, onCerrar, onCreada, tipoInicial }: Props) {
  const { t } = useTraduccion();
  const { avisar } = useAvisos();
  const cambios = useCambiosMarcas();
  const nueva = edicion?.tipo === 'nueva';
  const marca = edicion?.tipo === 'renombrar' ? edicion.marca : null;
  const tipos = useTiposDeActivo();
  const todas = useTodasLasMarcas(nueva);

  const form = useFormulario<Valores>({
    inicial: { name: marca?.name ?? '', assetTypeId: tipoInicial ?? '' },
    validar: (v) => ({ ...(!v.name.trim() && { name: 'validation.required' }) }),
    guardar: (v) =>
      marca
        ? cambios.renombrarMarca(String(marca.id), version(marca.version), v.name)
        : cambios.crearMarca(v.name, v.assetTypeId || null, onCreada),
    camposPorCodigo: {
      'asset_brand.nombre_duplicado': 'name',
      'asset_type.no_encontrado': 'assetTypeId',
    },
  });

  // Otra marca con el mismo nombre y otro tipo. La base la deja pasar; la
  // pantalla lo dice para que no se cree la misma dos veces por descuido.
  const parecida = nueva
    ? (todas.data ?? []).find(
        (m) =>
          form.valores.name.trim() !== '' &&
          mismoNombre(m.name, form.valores.name) &&
          String(m.assetTypeId ?? '') !== form.valores.assetTypeId,
      )
    : undefined;

  async function guardar() {
    const nombre = form.valores.name.trim();
    if (!(await form.enviar())) return;
    avisar({
      titulo: marca
        ? t('admin.marcas.aviso.marcaRenombrada', { nombre })
        : t('admin.marcas.aviso.marcaCreada', { nombre }),
    });
    onCerrar();
  }

  function cerrar() {
    if (form.guardando) return;
    onCerrar();
  }

  const opcionesTipo = [
    { valor: '', etiqueta: t('admin.marcas.tipo.generica') },
    ...(tipos.data ?? []).filter((tipo) => tipo.isActive).map((tipo) => ({ valor: String(tipo.id), etiqueta: tipo.name })),
  ];

  return (
    <Ventana
      abierta={edicion !== null}
      titulo={marca ? t('admin.marcas.ventanaMarca.renombrar') : t('admin.marcas.ventanaMarca.nueva')}
      info={marca ? t('admin.marcas.ventanaMarca.renombrarTexto') : t('admin.marcas.ventanaMarca.nuevaTexto')}
      acciones={
        <>
          <Boton variante="texto" disabled={form.guardando} onClick={cerrar}>
            {t('app.cancelar')}
          </Boton>
          <Boton disabled={form.guardando} aria-busy={form.guardando} onClick={() => void guardar()}>
            {marca ? t('admin.marcas.guardar') : t('admin.marcas.ventanaMarca.crear')}
          </Boton>
        </>
      }
    >
      {form.aviso && <AvisoError aviso={form.aviso} />}
      <CampoTexto
        form={form}
        nombre="name"
        etiqueta={t('admin.marcas.nombre')}
        obligatorio
        placeholder={t('admin.marcas.ventanaMarca.ejemplo')}
        autoComplete="off"
        ancho="completo"
      />
      {nueva && (
        <CampoSeleccion
          form={form}
          nombre="assetTypeId"
          etiqueta={t('admin.marcas.tipo')}
          info={t('admin.marcas.tipo.ayuda')}
          opciones={opcionesTipo}
          ancho="completo"
        />
      )}
      {parecida && (
        <p className="marcas__parecida" role="status">
          {parecida.assetTypeName
            ? t('admin.marcas.parecida.deTipo', { nombre: parecida.name, tipo: parecida.assetTypeName })
            : t('admin.marcas.parecida.generica', { nombre: parecida.name })}
        </p>
      )}
    </Ventana>
  );
}
